import { Link, useLocation } from 'react-router-dom';

import { LoadingState } from '../components/LoadingState.js';
import { useIsAdmin } from '../moderation/useIsAdmin.js';

/**
 * Gates an admin-only route. We render an explicit "restricted" message
 * rather than 404'ing so the URL stays debuggable — the `admins` table is
 * the single source of truth, and a wrong answer here should not look
 * like a routing bug.
 */
export function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { isAdmin, isLoading } = useIsAdmin();
  const location = useLocation();
  if (isLoading) return <LoadingState surface="admin-gate" />;
  if (!isAdmin) {
    return (
      <div className="space-y-3">
        <h1 className="text-2xl font-semibold">Admin</h1>
        <p className="text-stone-600">
          This surface is restricted to administrators. If you think you should have access, ask
          another admin to grant it.
        </p>
        <p className="text-xs text-stone-500">
          Path: <code>{location.pathname}</code>
        </p>
      </div>
    );
  }
  return <>{children}</>;
}

export function AdminTabs() {
  return (
    <nav
      aria-label="Admin sections"
      className="flex gap-3 border-b border-stone-200 dark:border-stone-700 text-sm"
    >
      <AdminTabLink to="/admin">Moderation</AdminTabLink>
      <AdminTabLink to="/admin/global-toc">Global ToC</AdminTabLink>
      <AdminTabLink to="/admin/global-toc/import">Import from library</AdminTabLink>
      <AdminTabLink to="/admin/nutrition">Nutrition</AdminTabLink>
      <AdminTabLink to="/admin/feedback">Feedback</AdminTabLink>
    </nav>
  );
}

function AdminTabLink({ to, children }: { to: string; children: React.ReactNode }) {
  const location = useLocation();
  // Tab matching:
  //   /admin                       → exact only (otherwise it would also
  //                                  light up on /admin/global-toc).
  //   /admin/global-toc            → exact, or any deeper path EXCEPT a
  //                                  sibling tab's prefix (`/import`).
  //   /admin/global-toc/import     → exact only.
  const path = location.pathname;
  let active: boolean;
  if (to === '/admin') {
    active = path === '/admin';
  } else if (to === '/admin/global-toc') {
    active = (path === to || path.startsWith(`${to}/`)) && path !== '/admin/global-toc/import';
  } else {
    active = path === to;
  }
  return (
    <Link
      to={to}
      className={`-mb-px rounded-t border-b-2 px-3 py-2 ${
        active
          ? 'border-stone-900 font-medium text-stone-900'
          : 'border-transparent text-stone-600 hover:text-stone-900'
      }`}
    >
      {children}
    </Link>
  );
}
